import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { safeAppRedirectPath } from '@/lib/auth-redirect'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { isAuthCallbackUrl } from '@/lib/auth-callback'
import { BRAND } from '@/lib/config/brand'
import { AuthLayout } from './auth-layout'
import { AuthFormAlert } from './auth-form-alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PasswordInput } from './password-input'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

const signupSchema = z
  .object({
    email: z.string().email('Enter a valid email'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type SignupFormValues = z.infer<typeof signupSchema>

export function SignupForm() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectAfterAuth = safeAppRedirectPath(searchParams.get('redirect'))
  const [generalError, setGeneralError] = useState<string | null>(null)
  // Defensive: some invite/recovery links land on /signup instead of being
  // caught by main.tsx's redirect at boot (e.g. browser back/forward keeping
  // stale hash params, or the hash arriving after initial module load).
  // Re-check here so we never dead-end a real invitee on "signups not allowed".
  const [redirectingToResetPassword] = useState(() => isAuthCallbackUrl())

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { email: '', password: '', confirmPassword: '' },
  })

  useEffect(() => {
    if (!redirectingToResetPassword) return
    window.location.replace(`/reset-password${window.location.search}${window.location.hash}`)
  }, [redirectingToResetPassword])

  if (redirectingToResetPassword) {
    return (
      <AuthLayout title={`Create your ${BRAND.displayName} account`}>
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Redirecting to finish setting your password...</p>
        </div>
      </AuthLayout>
    )
  }

  const onSubmit = async (values: SignupFormValues) => {
    if (!supabase) return
    setGeneralError(null)
    const { error } = await supabase.auth.signUp({
      email: values.email,
      password: values.password,
    })
    if (error) {
      setGeneralError(error.message)
      return
    }
    navigate(redirectAfterAuth ?? '/', { replace: true })
  }

  return (
    <AuthLayout title={`Create your ${BRAND.displayName} account`}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
          data-testid="auth-signup-form"
        >
          {generalError && <AuthFormAlert message={generalError} />}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput placeholder="••••••••" {...field} />
                </FormControl>
                <FormDescription className="text-muted-foreground">
                  Min 6 characters
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <PasswordInput placeholder="••••••••" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Create account'
            )}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link
              to={
                redirectAfterAuth
                  ? `/login?redirect=${encodeURIComponent(redirectAfterAuth)}`
                  : '/login'
              }
              className="text-primary underline-offset-4 hover:underline"
            >
              Log in
            </Link>
          </p>
        </form>
      </Form>
    </AuthLayout>
  )
}
